import { AI_ENDPOINT } from '@Env/variables';
import { createAxiosInstance } from './axios';
import {
  ImageGenerationRequest,
  ImageGenerationResponse,
  ImageHistoryResponse,
} from '@Models/types';
import { AxiosError } from 'axios';

export const imageGenerationAPI = (logout: () => void) => {
  const axios = createAxiosInstance(logout, AI_ENDPOINT);

  return {
    generateImage: async (
      params: ImageGenerationRequest
    ): Promise<ImageGenerationResponse> => {
      try {
        const res = await axios.post('/api/generateImage', params, {
          responseType: 'json',
        });
        const data =
          typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        return data as ImageGenerationResponse;
      } catch (err) {
        const error = err as AxiosError<{
          message?: string;
          error?: string;
          body?: string | { message?: string };
        }>;
        let message = 'Failed to generate image';

        if (error.response?.data) {
          const data = error.response.data;
          if (typeof data === 'string') {
            try {
              const parsed = JSON.parse(data);
              message = parsed.message || parsed.error || message;
            } catch {
              // ignore parse errors
            }
          } else if (data.message) {
            message = data.message;
          } else if (data.error) {
            message = data.error;
          } else if (data.body) {
            if (typeof data.body === 'string') {
              try {
                const parsed = JSON.parse(data.body);
                message = parsed.message || message;
              } catch {
                // ignore parse errors
              }
            } else if (data.body.message) {
              message = data.body.message;
            }
          }
        } else if (error.message) {
          message = error.message;
        }

        console.error('Image generation API error:', {
          status: error.response?.status,
          message,
          originalError: error.message,
        });

        throw new Error(message);
      }
    },

    getJobStatus: async (jobId: string): Promise<ImageGenerationResponse> => {
      try {
        const res = await axios.get(
          `/api/imageGenerationStatus?jobId=${encodeURIComponent(jobId)}`,
          { responseType: 'json' }
        );
        const data =
          typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        return data as ImageGenerationResponse;
      } catch (err) {
        const error = err as AxiosError<{
          message?: string;
          error?: string;
        }>;
        let message = 'Failed to check job status';

        if (error.response?.data) {
          const data = error.response.data;
          if (typeof data === 'string') {
            try {
              const parsed = JSON.parse(data);
              message = parsed.message || parsed.error || message;
            } catch {
              // ignore parse errors
            }
          } else if (data.message) {
            message = data.message;
          } else if (data.error) {
            message = data.error;
          }
        }

        throw new Error(message);
      }
    },

    getHistory: async (
      limit: number = 20,
      offset: number = 0
    ): Promise<ImageHistoryResponse> => {
      const res = await axios.get(
        `/api/imageGenerationHistory?limit=${limit}&offset=${offset}`,
        { responseType: 'json' }
      );
      const data =
        typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
      return data as ImageHistoryResponse;
    },

    deleteImage: async (payload: { refId: string; index?: number }) => {
      const res = await axios.delete('/api/deleteImage', {
        data: payload,
        responseType: 'json',
      });
      const data =
        typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
      return data;
    },
    getGenerationImages: async (refId: string): Promise<string[]> => {
      try {
        const res = await axios.get(
          `/api/getGenerationImages?refId=${encodeURIComponent(refId)}`,
          {
            responseType: 'json',
          }
        );
        const data =
          typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        return data.images || [];
      } catch (err) {
        const error = err as AxiosError<{
          message?: string;
          error?: string;
          body?: string | { message?: string };
        }>;
        let message = 'Failed to fetch generation images';

        if (error.response?.data) {
          const data = error.response.data as unknown;

          if (typeof data === 'string') {
            try {
              const parsed = JSON.parse(data);
              message = parsed.message || parsed.error || message;
            } catch {
              // ignore parse errors
            }
          } else if (data && typeof data === 'object') {
            const obj = data as {
              message?: string;
              error?: string;
              body?: string | { message?: string };
            };
            if (obj.message) {
              message = obj.message;
            } else if (obj.error) {
              message = obj.error;
            } else if (obj.body) {
              if (typeof obj.body === 'string') {
                try {
                  const parsed = JSON.parse(obj.body);
                  message = parsed.message || message;
                } catch {
                  // ignore parse errors
                }
              } else if (
                obj.body &&
                typeof obj.body === 'object' &&
                'message' in obj.body
              ) {
                message = (obj.body as { message?: string }).message || message;
              }
            }
          }
        }

        throw new Error(message);
      }
    },
  };
};
